import React, { ChangeEvent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import { addFilter, applyDefaults, removeFilter } from "redux/actions/filters/filters";
import { BillingFilterGrouping, ComponentKey, OrderFilterGrouping } from "redux/reducers/filters/types";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import { useFilterAccordionContext } from "components/accordion/Accordion";
import Checkbox from "components/form/checkbox/Checkbox";

import "./filterList.scss";

type FilterList = {
  /** Value provided by FilterAccordionContext takes precedent if present. */
  collection?: ComponentKey;
  defaultValues?: FinancialStatusKey[];
  condensed?: boolean;

  onChange?: (e: ChangeEvent<HTMLInputElement>, value: string) => void;
};

export type FinancialStatusKey =
  | "paid"
  | "partially_paid"
  | "refunded"
  | "partially_refunded"
  | "unpaid"
  | "voided"
  | "cancelled";

export type BillingFinancialStatusKey =
  | "paid"
  | "partially_paid"
  | "refunded"
  | "partially_refunded"
  | "unpaid"
  | "voided"
  | "cancelled";

const financialStatusFilters = (t: TFunction<"translation", undefined>) => [
  {
    id: "paid",
    label: t("secure.facility.order.order_filters.005"),
  },
  {
    id: "partially_paid",
    label: t("secure.facility.order.order_filters.007"),
  },
  {
    id: "refunded",
    label: t("secure.facility.order.order_filters.008"),
  },
  {
    id: "partially_refunded",
    label: t("secure.facility.order.order_filters.021"),
  },
  {
    id: "unpaid",
    label: t("secure.facility.order.order_filters.006"),
  },
  {
    id: "voided",
    label: t("secure.facility.order.order_filters.022"),
  },
  {
    id: "cancelled",
    label: t("secure.facility.order.order_filters.023"),
  },
];

const billingFinancialStatusFilters = (t: TFunction<"translation", undefined>) => [
  {
    id: "complete",
    label: t("Complete"),
  },
  {
    id: "cancelled",
    label: t("Cancelled"),
  },
  {
    id: "pending",
    label: t("Pending"),
  },
];

export default function FinancialStatusFilterList(props: FilterList) {
  const { t } = useTranslation();

  // const filters = useAppSelector(store =>  store.orderPageFilters);

  const dispatch = useAppDispatch();
  const { collectionContext } = useFilterAccordionContext();

  const activeCollection = collectionContext ?? props.collection;

  const billingFilters = useAppSelector(store => store.billingPageFilters);
  const orderFilters = useAppSelector(store => store.orderPageFilters);

  const filters = activeCollection === "billing" ? billingFilters : orderFilters;

  type FilterOption = { id: string; label: string };

  const labelArray = useMemo(() => {
    let active: FilterOption[];

    if (props.collection === "billing") {
      active = billingFinancialStatusFilters(t).filter(val =>
        filters.some(condition => condition.value.includes(val.id as BillingFinancialStatusKey)),
      );
    } else {
      active = financialStatusFilters(t).filter(val =>
        filters.some(condition => condition.value.includes(val.id as FinancialStatusKey)),
      );
    }

    return active.map(filter => filter.label);
  }, [filters, props.collection, t]);

  // const labelArray = useMemo(() => {
  //   let active;

  //   if (props.collection === "billing") {
  //     active = billingFinancialStatusFilters(t).filter(val =>
  //       filters.some(condition => condition.value.includes(val.id as BillingFinancialStatusKey)),
  //     );
  //   } else {
  //     active = financialStatusFilters(t).filter(val =>
  //       filters.some(condition => condition.value.includes(val.id as FinancialStatusKey)),
  //     );
  //   }

  //   return active.map(filter => filter.label);
  // }, [filters]);

  // Apply default filters
  useEffect(() => {
    if (!props.defaultValues) {
      return;
    }

    if (!filters.find(group => group.label === "financial_status")) {
      const defaultActive = financialStatusFilters(t).filter(val =>
        props.defaultValues.some(condition => condition === val.id),
      );

      if (props.collection === "billing") {
        const nDefaultActive = billingFinancialStatusFilters(t).filter(val =>
          props.defaultValues.some(condition => condition === val.id),
        );

        dispatch(
          applyDefaults<BillingFilterGrouping>({
            collectionName: collectionContext ? collectionContext : props.collection,
            key: "financial_status",
            value: props.defaultValues,
            displayLabels: nDefaultActive.map(filter => String(filter.label)),
          }),
        );
      } else {
        dispatch(
          applyDefaults<OrderFilterGrouping>({
            collectionName: collectionContext ? collectionContext : props.collection,
            key: "financial_status",
            value: props.defaultValues,
            displayLabels: defaultActive.map(filter => String(filter.label)),
          }),
        );
      }
    }
  }, [props.defaultValues]);

  function listItemClick(prevChecked: boolean, id: string) {
    if (!prevChecked) {
      if (props.collection === "billing") {
        const nNewFilter = billingFinancialStatusFilters(t).filter(val => val.id === id)[0];
        const nNewLabel = billingFinancialStatusFilters(t)
          .filter(val => labelArray.includes(String(val.label)) || val.label === nNewFilter.label)
          .map(ele => ele.label as string);
        dispatch(
          addFilter<BillingFilterGrouping>({
            collectionName: collectionContext ? collectionContext : props.collection,
            key: "financial_status",
            value: id,
            displayLabels: nNewLabel,
          }),
        );
      } else {
        const newFilter = financialStatusFilters(t).filter(val => val.id === id)[0];
        const newLabel = financialStatusFilters(t)
          .filter(val => labelArray.includes(String(val.label)) || val.label === newFilter.label)
          .map(ele => ele.label as string);

        dispatch(
          addFilter<OrderFilterGrouping>({
            collectionName: collectionContext ? collectionContext : props.collection,
            key: "financial_status",
            value: id,
            displayLabels: newLabel,
          }),
        );
      }

      return;
    }

    // remove filter condition if unchecked
    if (props.collection === "billing") {
      const nNewFilter = billingFinancialStatusFilters(t).filter(val => val.id === id)[0];

      dispatch(
        removeFilter<BillingFilterGrouping>({
          collectionName: collectionContext ? collectionContext : props.collection,
          key: "financial_status",
          value: id,
          displayLabels: labelArray.filter(val => val !== nNewFilter.label),
        }),
      );
    } else {
      const newFilter = financialStatusFilters(t).filter(val => val.id === id)[0];

      dispatch(
        removeFilter<OrderFilterGrouping>({
          collectionName: collectionContext ? collectionContext : props.collection,
          key: "financial_status",
          value: id,
          displayLabels: labelArray.filter(val => val !== newFilter.label),
        }),
      );
    }
  }

  return (
    <ul className={`filter-list${props.condensed ? " condensed" : ""}`}>
      {props.collection === "billing"
        ? billingFinancialStatusFilters(t).map((status, index) => {
            const checked = filters.some(condition => {
              if (condition.value.includes(status.id)) {
                return true;
              } else {
                return false;
              }
              // return condition.value.includes(status.id);
            });

            return (
              <li key={index} onClick={() => listItemClick(checked, status.id)}>
                <Checkbox
                  checked={checked}
                  ariaChecked={checked}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => e.stopPropagation()}
                  label={status.label}
                  size="extra small"
                />
              </li>
            );
          })
        : financialStatusFilters(t).map((status, index) => {
            const checked = filters.some(condition => {
              return condition.value.includes(status.id);
            });

            return (
              <li key={index} onClick={() => listItemClick(checked, status.id)}>
                <Checkbox
                  checked={checked}
                  ariaChecked={checked}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => e.stopPropagation()}
                  label={status.label}
                  size="extra small"
                />
              </li>
            );
          })}
    </ul>
  );
}
